import { Icon } from '@buggy/shared'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button, Flex, Spin, Tabs } from 'antd'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { WorkspacesAPITagType } from '@/api/api'
import {
  quotationApi,
  useDeleteQuotationMutation,
  useFetchCustomQuotationsInfiniteQuery,
  useFetchPredictiveReportQuotationsInfiniteQuery,
} from '@/api/workspace-api/quotation-api'
import CardErrorCosmos from '@/components/common/card/card-error-cosmos'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import AddQuotationModal from '@/components/kol/common/kol-cooperation-detail-drawer/quotation/add-quotation-modal'
import EditQuotationModal, {
  type EditQuotationModalProps,
} from '@/components/kol/common/kol-cooperation-detail-drawer/quotation/edit-quotation-modal'
import QuotationList, {
  type QuotationListProps,
} from '@/components/kol/common/kol-cooperation-detail-drawer/quotation/quotation-list'
import useNotification from '@/hooks/antd/use-notification'
import useLocalizedKolQuotationCurrency from '@/hooks/use-localized-kol-quotation-currency'
import { useDirectoryActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import logger from '@/utils/logger'

enum TabKey {
  PredictiveReport = 'predictiveReport',
  Custom = 'custom',
}

interface QuotationProps {
  kolInfo?: KolSchemaModel
}

const Quotation: FunctionComponent<QuotationProps> = ({ kolInfo }) => {
  const { formatMessage, formatNumber } = useIntl()
  const localizedKolQuotationCurrency = useLocalizedKolQuotationCurrency()
  const notification = useNotification()
  const dispatch = useDispatch()

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [customQuotationsPage, setCustomQuotationsPage] = useState(1)
  const [predictiveReportQuotationsPage, setPredictiveReportQuotationsPage] =
    useState(1)
  const [editingQuotation, setEditingQuotation] =
    useState<EditQuotationModalProps['quotation']>()

  const kolId = kolInfo?.id

  const directoryActionPermission = useDirectoryActionPermission()
  const {
    data: fetchCustomQuotationsResponse,
    isLoading: isLoadingCustomQuotations,
    isFetching: isFetchingCustomQuotations,
    isError: isErrorCustomQuotations,
  } = useFetchCustomQuotationsInfiniteQuery(
    kolId
      ? {
          kolId,
          page: customQuotationsPage,
        }
      : skipToken,
  )
  const {
    data: fetchPredictiveReportQuotationsResponse,
    isLoading: isLoadingPredictiveReportQuotations,
    isFetching: isFetchingPredictiveReportQuotations,
    isError: isErrorPredictiveReportQuotations,
  } = useFetchPredictiveReportQuotationsInfiniteQuery(
    kolId
      ? {
          kolId,
          page: predictiveReportQuotationsPage,
        }
      : skipToken,
  )
  const [deleteQuotation] = useDeleteQuotationMutation()

  const isLoading =
    isLoadingCustomQuotations || isLoadingPredictiveReportQuotations || !kolInfo
  const isError = isErrorCustomQuotations || isErrorPredictiveReportQuotations

  const handleInvalidateCustomQuotations = (): void => {
    setCustomQuotationsPage(1)

    dispatch(
      quotationApi.util.invalidateTags([
        WorkspacesAPITagType.DirectoryQuotation,
      ]),
    )
  }

  const handleDeleteQuotation: QuotationListProps['onDelete'] = async (
    quotation,
  ): Promise<void> => {
    if (!kolId) {
      return
    }

    try {
      await deleteQuotation({
        kolId,
        quotationId: quotation.id,
      }).unwrap()

      handleInvalidateCustomQuotations()

      notification.success({
        message: formatMessage({
          id: 'general:operation_success',
        }),
        placement: 'bottomRight',
      })
    } catch (error) {
      logger.error(error)
      notification.error({
        message: formatMessage({
          id: 'general:operation_failure',
        }),
        placement: 'bottomRight',
      })
    }
  }

  if (isLoading) {
    return (
      <Container>
        <Spin />
      </Container>
    )
  }

  if (isError) {
    return (
      <Container>
        <CardErrorCosmos />
      </Container>
    )
  }

  return (
    <Container>
      <StyledTabs
        items={[
          {
            key: TabKey.PredictiveReport,
            label: formatMessage(
              {
                id: 'kol_cooperation_detail_drawer:tabs_predictive_report_quotation',
              },
              {
                predictiveReportCount: formatNumber(
                  fetchPredictiveReportQuotationsResponse?.data.total ?? 0,
                ),
              },
            ),
            children: (
              <QuotationList
                data={
                  fetchPredictiveReportQuotationsResponse?.data
                    .predictive_report_quotation_list
                }
                hasNextPage={
                  !isEmpty(fetchPredictiveReportQuotationsResponse?.data) &&
                  fetchPredictiveReportQuotationsResponse.data.page <
                    (fetchPredictiveReportQuotationsResponse.data.total_page ??
                      0)
                }
                isLoadMore={isFetchingPredictiveReportQuotations}
                onLoadMore={(): void => {
                  setPredictiveReportQuotationsPage((prevPage) => prevPage + 1)
                }}
              />
            ),
          },
          {
            key: TabKey.Custom,
            label: formatMessage(
              {
                id: 'kol_cooperation_detail_drawer:tabs_custom_quotation',
              },
              {
                customQuotationCount: formatNumber(
                  fetchCustomQuotationsResponse?.data?.total ?? 0,
                ),
              },
            ),
            children: (
              <QuotationList
                data={fetchCustomQuotationsResponse?.data.quotation_list}
                hasNextPage={
                  !isEmpty(fetchCustomQuotationsResponse?.data) &&
                  fetchCustomQuotationsResponse.data.page <
                    (fetchCustomQuotationsResponse.data.total_page ?? 0)
                }
                isLoadMore={isFetchingCustomQuotations}
                onClickEdit={setEditingQuotation}
                onDelete={handleDeleteQuotation}
                onLoadMore={(): void => {
                  setCustomQuotationsPage((prevPage) => prevPage + 1)
                }}
              />
            ),
          },
        ]}
        tabBarExtraContent={{
          right: (
            <Flex align='center' gap={20}>
              <CurrencyLabel>{localizedKolQuotationCurrency}</CurrencyLabel>
              <PermissionInsufficientTooltip
                insufficient={!directoryActionPermission}
              >
                <Button
                  disabled={!directoryActionPermission}
                  icon={<Icon name='plus' />}
                  onClick={() => setAddModalOpen(true)}
                >
                  {formatMessage({
                    id: 'autoreport:chart_field_add_btn',
                  })}
                </Button>
              </PermissionInsufficientTooltip>
            </Flex>
          ),
        }}
        tabBarGutter={20}
      />
      <AddQuotationModal
        kolInfo={kolInfo}
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={handleInvalidateCustomQuotations}
      />
      {editingQuotation && kolId && (
        <EditQuotationModal
          kolId={kolId}
          quotation={editingQuotation}
          onClose={() => setEditingQuotation(undefined)}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 16px 24px 32px 24px;
`

const StyledTabs = styled(Tabs)`
  &&&.ant-tabs {
    flex: 1;

    .ant-tabs-nav {
      border: none;
      margin: 0 0 16px;

      &::before {
        display: none;
      }
    }

    .ant-tabs-nav-wrap {
      padding: 0;

      .ant-tabs-nav-list {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        .ant-tabs-tab-active {
          border-radius: 30px;
          border: 1px solid ${({ theme }): string => theme.colors.brand.primary};
        }
      }

      .ant-tabs-ink-bar {
        display: none;
      }
    }
  }
`

const CurrencyLabel = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`

export default Quotation
